/**
 * chartFileLink.ts
 *
 * How chord chart entries dated to a MIDI file (midiSource, see
 * CHORD-TIMESTAMP-SPEC.md) stay in step with the file's chord regions
 * (midiAnalysis.ts). The rule, from PLAN.md: the file flows to the chart
 * automatically, but only into what the user has not changed in the chart;
 * the chart never flows to the file on its own.
 *
 * - An entry is *linked* to a region when it is dated to the file and to the
 *   region's start. Nothing else is stored, so a region's link survives
 *   anything that keeps its start.
 * - A linked entry *follows* its region while it holds the region's notes and
 *   one of the region's two names (FEEL or READ, so switching the setting is
 *   not an edit). That is judged against the region as it was before each
 *   edit, so nothing has to record that an entry was edited.
 *
 * Pure functions over the entries they need, so the rules can be tested
 * without rendering a chart.
 */

import { ChordSegment } from './chordChartExport';
import { ChordRegion, isEnabled, regionVoicing } from './midiAnalysis';
import { KeyCenter, KeyCenterRegion, keyCentersEqual, regionsInChartOrder } from './keyCenterRegions';

export type MidiSource = { fileKey: string; seconds: number };

/** All this module needs of a chart entry. */
export type DatedEntry = {
  id: string;
  isSeparator: boolean;
  name: string;
  voicing: number[];
  segment?: ChordSegment;
  midiSource?: MidiSource;
};

/** One region as the player last reported it and as it is now. No `before`:
 * a region new to the chart's view (a split, or the file just loaded). No
 * `after`: a region merged away. */
export type RegionChange = { before?: ChordRegion; after?: ChordRegion };

/** Two times closer than this are the same moment (the backend rounds to 0.1 ms). */
const SAME_TIME = 0.001;

export function linkedTo(entry: DatedEntry, fileKey: string, region: ChordRegion): boolean {
  return !entry.isSeparator && !!entry.midiSource && entry.midiSource.fileKey === fileKey
    && Math.abs(entry.midiSource.seconds - region.start) < SAME_TIME;
}

/** Whether any entry is linked to the region. */
export function isCharted(entries: DatedEntry[], fileKey: string, region: ChordRegion): boolean {
  return entries.some(entry => linkedTo(entry, fileKey, region));
}

/** An entry of the file dated to no region's start: its region was merged
 * away or moved while the entry did not follow it. */
export function isOrphan(entry: DatedEntry, fileKey: string, regions: ChordRegion[]): boolean {
  return !entry.isSeparator && !!entry.midiSource && entry.midiSource.fileKey === fileKey
    && !regions.some(region => linkedTo(entry, fileKey, region));
}

const sameNotes = (a: number[], b: number[]) => {
  const x = Array.from(new Set(a)).sort((p, q) => p - q);
  const y = Array.from(new Set(b)).sort((p, q) => p - q);
  return x.length === y.length && x.every((note, i) => note === y[i]);
};

// The fields that make a chord symbol what it is; `weight` is a score, not part of the name
export const sameSegment = (a: ChordSegment | null | undefined, b: ChordSegment | null | undefined) =>
  !!a && !!b && a.root_pc === b.root_pc && a.bass_pc === b.bass_pc && a.mode === b.mode
  && a.chord_qual === b.chord_qual && a.third_quality === b.third_quality && a.extension === b.extension;

/** Whether the entry holds the region's notes, corrections included. */
export const holdsNotesOf = (entry: DatedEntry, region: ChordRegion) => sameNotes(entry.voicing, regionVoicing(region));

/** Whether the entry still holds the region's notes and one of its names. */
export function follows(entry: DatedEntry, region: ChordRegion): boolean {
  return holdsNotesOf(entry, region)
    && (sameSegment(entry.segment, region.segment_feel) || sameSegment(entry.segment, region.segment_read));
}

/**
 * The chart after the player's report of region changes:
 * - an entry linked to a region whose start moved is re-dated to the new start;
 * - one that followed the region takes its new notes and the name of the same
 *   setting (FEEL or READ) it held, named by `nameOf`, or the name `shownOf`
 *   gives when given (Follow: the setting the player shows). Not when the
 *   region no longer has a chord to chart (a single pitch class, or a scale):
 *   it keeps what it had, and no longer follows;
 * - one whose region was merged away is deleted if it followed, and otherwise
 *   kept as an orphan, still dated where it was;
 * - one whose region was hidden is deleted if it followed, and otherwise kept,
 *   still linked to the hidden region.
 * Returns `entries` itself when nothing changed.
 */
export function applyRegionChanges<E extends DatedEntry>(
  entries: E[], fileKey: string, changes: RegionChange[], nameOf: (segment: ChordSegment) => string,
  shownOf?: (region: ChordRegion) => ChordSegment | null | undefined,
): E[] {
  let changed = false;
  const next: E[] = [];
  entries.forEach(entry => {
    const change = changes.find(c => c.before !== undefined && linkedTo(entry, fileKey, c.before));
    if (!change || !change.before) {
      next.push(entry);
      return;
    }
    const before = change.before;
    const after = change.after;
    const followed = follows(entry, before);
    if (followed && (!after || (isEnabled(before) && !isEnabled(after)))) {
      changed = true;
      return;
    }
    if (!after) {
      changed = true;
      next.push(entry);
      return;
    }
    let updated: E = entry;
    if (Math.abs(after.start - before.start) >= SAME_TIME) {
      updated = { ...updated, midiSource: { fileKey, seconds: after.start } };
    }
    const tookRead = sameSegment(entry.segment, before.segment_read) && !sameSegment(entry.segment, before.segment_feel);
    const segment = shownOf ? shownOf(after) : (tookRead ? after.segment_read : after.segment_feel);
    const current = shownOf ? holdsNotesOf(entry, after) && sameSegment(entry.segment, segment) : follows(entry, after);
    if (followed && segment && !after.is_scale && !current) {
      updated = { ...updated, voicing: regionVoicing(after).slice(), segment, name: nameOf(segment) };
    }
    if (updated !== entry) changed = true;
    next.push(updated);
  });
  return changed ? next : entries;
}

/**
 * The chart after the player's READ / FEEL setting changed while Follow is
 * on: each entry following its region takes the name `shownOf` gives, when the
 * region has one yet (the rest take it from the report that names them).
 * Returns `entries` itself when nothing changed.
 */
export function respellFollowed<E extends DatedEntry>(
  entries: E[], fileKey: string, regions: ChordRegion[], shownOf: (region: ChordRegion) => ChordSegment | null | undefined,
  nameOf: (segment: ChordSegment) => string,
): E[] {
  let changed = false;
  const next = entries.map(entry => {
    const region = regions.find(r => linkedTo(entry, fileKey, r));
    if (!region || region.is_scale || !follows(entry, region)) return entry;
    const segment = shownOf(region);
    if (!segment || sameSegment(entry.segment, segment)) return entry;
    changed = true;
    return { ...entry, segment, name: nameOf(segment) };
  });
  return changed ? next : entries;
}

/**
 * Inserts promoted regions' entries (in time order). Each goes after the last
 * entry linked to the region before it, when that one is charted, so a split
 * region lands beside its neighbour and a run promoted together stays in
 * order; failing that, before the first entry linked to the region after it.
 * Otherwise it goes at the insertion point: after the selected entry,
 * or at the end, moving on past each entry put there. `leadingBarline` goes
 * in before the first entry put at the very end of the chart when the entry
 * before is not a barline (`isBarline`), as a chord added at the end does.
 * Returns the chart and the index of the last entry added.
 */
export function placePromoted<E extends DatedEntry>(
  entries: E[], fileKey: string, regions: ChordRegion[], added: { region: ChordRegion; entry: E }[],
  selectedIndex: number | null, leadingBarline: E, isBarline: (entry: E) => boolean,
): { entries: E[]; last: number } {
  const next = entries.slice();
  let cursor = selectedIndex === null ? next.length : selectedIndex + 1;
  let barlineDone = false;
  let lastEntry: E | null = null;
  added.forEach(({ region, entry }) => {
    const k = regions.findIndex(r => r.index === region.index);
    const previous = k > 0 ? regions[k - 1] : null;
    const following = k >= 0 && k < regions.length - 1 ? regions[k + 1] : null;
    let at = -1;
    if (previous) {
      for (let i = next.length - 1; i >= 0 && at < 0; i--) {
        if (linkedTo(next[i], fileKey, previous)) at = i + 1;
      }
    }
    if (following) {
      for (let i = 0; i < next.length && at < 0; i++) {
        if (linkedTo(next[i], fileKey, following)) at = i;
      }
    }
    if (at < 0) {
      at = cursor;
      if (!barlineDone && at === next.length && at > 0 && !isBarline(next[at - 1])) {
        next.splice(at, 0, leadingBarline);
        at++;
        cursor++;
      }
      barlineDone = true;
    }
    next.splice(at, 0, entry);
    if (at <= cursor) cursor++;
    lastEntry = entry;
  });
  return { entries: next, last: lastEntry === null ? -1 : next.indexOf(lastEntry) };
}

/**
 * What the file says at one region's start: a key, or null where its global
 * key applies (before any key, or after a "use global key"); a key equal to
 * the global key is still that key. `changeStart`: where the file's key
 * change in force there starts, a key or a "use global key" marker alike
 * (null before any), so the chart can mirror every one of them, not only
 * those that change the key.
 */
export type FileKeyAt = { start: number; keyCenter: KeyCenter | null; changeStart: number | null };

/** The file's keys as the chart needs them: its global key, and each region's. */
export type FileKeys = { fileKey: string; globalKey: KeyCenter | null; regionKeys: FileKeyAt[] };

function fileKeyAtEntry(entry: DatedEntry, keys: FileKeys): FileKeyAt | undefined {
  if (entry.isSeparator || !entry.midiSource || entry.midiSource.fileKey !== keys.fileKey) return undefined;
  const seconds = entry.midiSource.seconds;
  return keys.regionKeys.find(r => Math.abs(r.start - seconds) < SAME_TIME);
}

/** The file's key at the region an entry is dated to; undefined when it is not dated to one. */
export function fileKeyOfEntry(entry: DatedEntry, keys: FileKeys): KeyCenter | null | undefined {
  const at = fileKeyAtEntry(entry, keys);
  return at === undefined ? undefined : at.keyCenter;
}

/**
 * Where the file's key changes land in the chart, as the chart would show
 * them if it only mirrored the file: walking the chart in order, an auto
 * region on each chord dated to the file whose key change (`changeStart`)
 * differs from that of the chord dated to it before, so the first charted
 * chord of each change, a key or a "use global key" (an auto "none") alike.
 * The two global keys are not compared: where the file's applies, the chart's
 * does, whatever each is. By chord ID.
 */
export function fileKeyMirror(entries: DatedEntry[], keys: FileKeys): Map<string, KeyCenterRegion> {
  const mirror = new Map<string, KeyCenterRegion>();
  let last: number | null = null;
  entries.forEach(entry => {
    const at = fileKeyAtEntry(entry, keys);
    if (at === undefined) return;
    if (at.changeStart !== last) mirror.set(entry.id, { startChordId: entry.id, keyCenter: at.keyCenter, source: 'auto' });
    last = at.changeStart;
  });
  return mirror;
}

// Whether two key changes at one chord are the same: both absent, or the same key
const sameKeyChange = (a: KeyCenterRegion | undefined, b: KeyCenterRegion | undefined) =>
  (a === undefined && b === undefined) || (a !== undefined && b !== undefined && keyCentersEqual(a.keyCenter, b.keyCenter));

/**
 * The chart's key centers after the file's keys changed from `before` to
 * `after` (and the chart from `prevEntries` to `entries`, `regions` already
 * reconciled to it), by the rule chords follow: the file flows to the chart
 * automatically, but only into what the user has not changed there. Nothing
 * records a change: it is judged against the file's keys as they were.
 *
 * At each chord dated to the file whose mirrored key change differs between
 * the two (fileKeyMirror), the chart takes the new one while it still held
 * the old one, whoever set it: an auto region added, changed or removed. A
 * chord new to the chart takes the file's as it is. Where the chart differed
 * from the file before the change (a key the user set or cleared there, not
 * yet settled) it stays as it is, and the difference shows as a marker
 * (divergentChartKeys) until the user settles it. `before` null (or another
 * file's): the chart had nothing of this file's yet.
 */
export function syncFileKeyRegions(
  prevEntries: DatedEntry[], entries: DatedEntry[], regions: KeyCenterRegion[], before: FileKeys | null, after: FileKeys,
): KeyCenterRegion[] {
  const prevIds = new Set(prevEntries.map(entry => entry.id));
  // The chart as it was against the file's keys as they were: what each chord
  // held while it followed
  const was = before !== null && before.fileKey === after.fileKey ? fileKeyMirror(prevEntries, before) : new Map<string, KeyCenterRegion>();
  const now = fileKeyMirror(entries, after);
  const byAnchor = new Map(regions.map(region => [region.startChordId, region]));
  let next = regions;
  entries.forEach(entry => {
    if (fileKeyAtEntry(entry, after) === undefined) return;
    const current = byAnchor.get(entry.id);
    const mirrored = now.get(entry.id);
    const followed = !prevIds.has(entry.id) || sameKeyChange(current, was.get(entry.id));
    if (!followed || sameKeyChange(current, mirrored)) return;
    next = next.filter(region => region.startChordId !== entry.id);
    if (mirrored !== undefined) next = [...next, mirrored];
  });
  return next === regions ? regions : regionsInChartOrder(entries, next);
}

/**
 * The chart back on the file's key at one chord: whatever key change the
 * chart has there goes, and the file's, if one lands there, takes its place.
 */
export function returnToFileKeyAt(
  entries: DatedEntry[], regions: KeyCenterRegion[], keys: FileKeys, chordId: string,
): KeyCenterRegion[] {
  const mirrored = fileKeyMirror(entries, keys).get(chordId);
  const without = regions.filter(region => region.startChordId !== chordId);
  return regionsInChartOrder(entries, mirrored === undefined ? without : [...without, mirrored]);
}

/** Where the chart and the file differ about a key, for a marker on the key lane. */
export type DivergentKey = {
  // The file time the marker shows at, and the chart chord the chart's key
  // change is on (or would be, where the chart cleared the file's)
  seconds: number;
  chordId: string;
  // The chart's key change there; `cleared`: none, where the file has one
  keyCenter: KeyCenter | null;
  cleared: boolean;
  // Where the file's key change landing at this chord starts, if one does:
  // the chart's key is taken into the file there
  changeStart: number | null;
  // The chart chord not in the file that the chart's key is set on, if it is
  addedChord: string | null;
};

/**
 * Where the chart's keys differ from the file's, each to be settled by the
 * user either way. Two kinds:
 *
 * - The user's own keys. One set on a chord dated to the file is compared
 *   there; one set on a chord that is not (a chord added in the chart) at the
 *   first chord dated to the file before the next key change in the chart.
 *   One that ends before reaching the file is the chart's alone.
 * - The file's key changes the chart does not show as they are: cleared by
 *   the user, or held back while unlinked ("Keep chart"), and auto regions of
 *   the file's the file no longer has there.
 *
 * Where both are on their global keys ("use global key", or no key yet) they
 * agree: the two global keys are compared once, by the global key's own
 * marker. Otherwise the keys themselves are compared, so an explicit C agrees
 * with a global key of C. One marker at a time.
 */
export function divergentChartKeys(
  entries: DatedEntry[], regions: KeyCenterRegion[], keys: FileKeys, chartGlobalKey: KeyCenter | null,
): DivergentKey[] {
  const ordered = regionsInChartOrder(entries, regions);
  const byAnchor = new Map(ordered.map(region => [region.startChordId, region]));
  const mirror = fileKeyMirror(entries, keys);
  const found: DivergentKey[] = [];
  const differs = (chartKey: KeyCenter | null, fileKey: KeyCenter | null) =>
    !(chartKey === null && fileKey === null)
    && !keyCentersEqual(chartKey === null ? chartGlobalKey : chartKey, fileKey === null ? keys.globalKey : fileKey);
  const add = (marker: DivergentKey) => {
    if (!found.some(other => Math.abs(other.seconds - marker.seconds) < SAME_TIME)) found.push(marker);
  };
  const changeStartAt = (entry: DatedEntry) => {
    const at = fileKeyAtEntry(entry, keys);
    return mirror.has(entry.id) && at !== undefined ? at.changeStart : null;
  };

  ordered.forEach(region => {
    if (region.source !== 'manual') return;
    const at = entries.findIndex(entry => entry.id === region.startChordId);
    if (at < 0) return;
    let reached: DatedEntry | null = null;
    for (let i = at; i < entries.length && reached === null; i++) {
      if (i > at && byAnchor.has(entries[i].id)) break;
      if (fileKeyAtEntry(entries[i], keys) !== undefined) reached = entries[i];
    }
    if (reached === null || !reached.midiSource) return;
    if (!differs(region.keyCenter, fileKeyOfEntry(reached, keys) as KeyCenter | null)) return;
    add({
      seconds: reached.midiSource.seconds, chordId: region.startChordId, keyCenter: region.keyCenter, cleared: false,
      changeStart: reached === entries[at] ? changeStartAt(reached) : null,
      addedChord: reached === entries[at] ? null : entries[at].name,
    });
  });

  // What the chart says at each chord: the key change covering it (null:
  // its global key)
  let inForce: KeyCenter | null = null;
  entries.forEach(entry => {
    const region = byAnchor.get(entry.id);
    if (region !== undefined) inForce = region.keyCenter;
    const at = fileKeyAtEntry(entry, keys);
    if (at === undefined || !entry.midiSource || (region !== undefined && region.source === 'manual')) return;
    const mirrored = mirror.get(entry.id);
    if (mirrored === undefined && region === undefined) return;
    if (sameKeyChange(region, mirrored) || !differs(inForce, at.keyCenter)) return;
    add({
      seconds: entry.midiSource.seconds, chordId: entry.id, keyCenter: inForce, cleared: region === undefined,
      changeStart: changeStartAt(entry), addedChord: null,
    });
  });
  return found;
}
